import type { AvailableGroup, Product } from "@innoclouds/api";
import type { ComboEntry, GroupEntry, ProductEntry } from "@innoclouds/db";
import { defu } from "defu";

export const isVisibleEntity = <T extends ProductEntry | Product | GroupEntry | AvailableGroup>(entity?: T | null): entity is T =>
	Boolean(entity && !entity.is_force_hidden_with_allowed_creation);

export const isRootGroup = <T extends GroupEntry | AvailableGroup>(group?: T | null): group is T => Boolean(isVisibleEntity(group) && !group?.parent_group);

export type HotmenuGroup = GroupEntry;

export type HotmenuMaps = {
	groups: Map<string, HotmenuGroup>;
	products: Map<string, ProductEntry>;
	combos?: Map<string, ComboEntry>;
};

export type MenuSectionProduct = ProductEntry & { is_group: false; parent: string };
export type MenuSectionCombo = ComboEntry & { is_group: false; is_combo: true; parent: string };
export type MenuSectionFolder = HotmenuGroup & { is_group: true; rootId: string; parent: string };
export type MenuSectionItem = MenuSectionProduct | MenuSectionCombo | MenuSectionFolder;

export type PreparedMenuGroup = HotmenuGroup & {
	products: MenuSectionItem[];
	breadcrumbs?: Array<{ id: string }>;
	rootId: string;
};

function* walkGroupTree(groupId: string, groups: Map<string, HotmenuGroup>, seen = new Set<string>(), isStart = true): Generator<HotmenuGroup> {
	if (seen.has(groupId)) return;
	seen.add(groupId);

	const group = groups.get(groupId);
	if (!group) return;
	if (!isStart && !isVisibleEntity(group)) return;

	yield group;
	for (const childId of group.child_groups || []) {
		yield* walkGroupTree(childId, groups, seen, false);
	}
}

export const collectDescendantProductIds = (group: HotmenuGroup, groups: Map<string, HotmenuGroup>): string[] => {
	const ids: string[] = [];
	const seen = new Set<string>();

	for (const current of walkGroupTree(group.id, groups)) {
		for (const id of current.products_ids ?? []) {
			if (seen.has(id)) continue;
			seen.add(id);
			ids.push(id);
		}
	}

	return ids;
};

const getVisibleChildGroups = (group: HotmenuGroup, groups: Map<string, HotmenuGroup>): HotmenuGroup[] =>
	(group.child_groups || []).map((id) => groups.get(id)).filter(isVisibleEntity);

const resolveGroupView = (group: HotmenuGroup, groups: Map<string, HotmenuGroup>, isFlat: boolean) => {
	const tags = new Set<string>(group.tags || []);
	const locTags: HotmenuGroup["loc_tags"][] = [];

	if (!isFlat) {
		return {
			tags: Array.from(tags),
			locTags: group.loc_tags,
			childs: getVisibleChildGroups(group, groups),
		};
	}

	for (const descendant of walkGroupTree(group.id, groups)) {
		descendant.tags?.forEach((tag) => void tags.add(tag));
		if (descendant.loc_tags && Object.keys(descendant.loc_tags).length) locTags.push(descendant.loc_tags);
	}

	return { tags: Array.from(tags), locTags: defu({}, ...locTags), childs: [] as HotmenuGroup[] };
};

export const hasVisibleContent = (group: HotmenuGroup, isFlat: boolean, maps: HotmenuMaps): boolean => {
	if (group.is_combo_group) return (maps.combos?.size ?? 0) > 0;

	if (!isFlat && getVisibleChildGroups(group, maps.groups).length) return true;

	const ids = isFlat ? collectDescendantProductIds(group, maps.groups) : (group.products_ids ?? []);
	return ids.some((id) => isVisibleEntity(maps.products.get(id)));
};

export const resolveBreadcrumbs = <T extends { id: string }>(history: T[] | undefined, isFlat: boolean, maps: HotmenuMaps): T[] | undefined => {
	if (!history?.length) return undefined;
	if (history.some((item) => !isVisibleEntity(maps.groups.get(item.id)))) return undefined;
	if (maps.groups.get(history[history.length - 1].id)?.is_combo_group) return history;
	if (!maps.products.size) return history;

	const trimmed = history.slice();
	while (trimmed.length > 1) {
		const current = maps.groups.get(trimmed[trimmed.length - 1].id);
		if (isVisibleEntity(current) && hasVisibleContent(current, isFlat, maps)) break;
		trimmed.pop();
	}

	return trimmed.length > 1 ? trimmed : undefined;
};

export type PrepareGroupMenuParams = HotmenuMaps & {
	group: HotmenuGroup;
	rootId: string;
	isFlat: boolean;
	breadcrumbs?: Array<{ id: string }>;
};

export const prepareGroupMenu = ({ group, rootId, isFlat, groups, products, combos, breadcrumbs }: PrepareGroupMenuParams): PreparedMenuGroup => {
	const current = breadcrumbs?.length ? (groups.get(breadcrumbs[breadcrumbs.length - 1].id) ?? group) : group;

	if (current.is_combo_group) {
		const comboProducts: MenuSectionCombo[] = Array.from(combos?.values() ?? []).map((combo) => ({
			...combo,
			is_group: false,
			is_combo: true,
			parent: combo.id,
		}));
		return { ...current, products: comboProducts, breadcrumbs, rootId, name: group.name, loc_name: group.loc_name };
	}

	const { tags, locTags, childs } = resolveGroupView(current, groups, isFlat);
	const children: MenuSectionFolder[] = childs.map((child) => ({ ...child, rootId, parent: group.id, is_group: true }));
	const productItems: MenuSectionProduct[] = (isFlat ? collectDescendantProductIds(current, groups) : (current.products_ids ?? []))
		.map((id) => products.get(id))
		.filter(isVisibleEntity)
		.map((product) => ({ ...product, parent: product.id, is_group: false }));

	return {
		...current,
		products: [...children, ...productItems],
		breadcrumbs,
		rootId,
		name: group.name,
		loc_name: group.loc_name,
		tags,
		loc_tags: locTags,
	};
};
